import type { IntlShape, MessageDescriptor } from 'react-intl';
import { BaseStyleId, DynamicStyleId, StaticStyleId, StyleId } from '../types/Styles';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from './styleCardConfigs';

export const isBaseStyle = (id: StyleId): id is BaseStyleId => {
  return Object.keys(baseStyleCardConfigs).includes(id);
};

export const isStaticStyle = (id: StyleId): id is StaticStyleId => {
  return Object.keys(staticStyleCardConfigs).includes(id);
};

export const isDynamicStyle = (id: StyleId): id is DynamicStyleId => {
  return Object.keys(dynamicStyleCardConfigs).includes(id);
};

export const styleSupports3d = (style: BaseStyleId) => {
  return baseStyleCardConfigs[style].supports3d;
};

/** The card config for any style id, without the caller re-deriving which of the three it lives in. */
export const getStyleCardConfig = (id: StyleId) =>
  isBaseStyle(id) ? baseStyleCardConfigs[id] : isStaticStyle(id) ? staticStyleCardConfigs[id] : dynamicStyleCardConfigs[id];

/** The applied-style slices of map state, as every layer menu selects them. */
type AppliedStyleIds = {
  baseStyleId?: BaseStyleId;
  dynamicStyleIds: DynamicStyleId[];
  staticStyleIds: StaticStyleId[];
};

/** Whether a style is applied, without the caller branching on which of the three kinds it is. */
export const isStyleApplied = (id: StyleId, { baseStyleId, dynamicStyleIds, staticStyleIds }: AppliedStyleIds) =>
  isBaseStyle(id) ? baseStyleId === id : isStaticStyle(id) ? staticStyleIds.includes(id) : dynamicStyleIds.includes(id);

/** Card configs carry plain strings for the admin-only labels that are never translated, descriptors for the rest. */
export const formatStyleDisplayName = (intl: IntlShape, displayName: string | MessageDescriptor) =>
  typeof displayName === 'string' ? displayName : intl.formatMessage(displayName);
